import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/shared/components/ui/card";
import { Badge } from "@/shared/components/ui/badge";
import { Separator } from "@/shared/components/ui/separator";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/shared/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/shared/components/ui/select";
import { useToast } from "@/shared/hooks/use-toast";
import { apiClient } from "@/shared/services/apiClient";
import { API_ROUTES } from "@/shared/services/apiRoutes";
import { useSubscription } from "@/features/billing/contexts/SubscriptionContext";

export default function SubscriptionManagementPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { usage, loading, refreshSubscription } = useSubscription();
  const [isProcessing, setIsProcessing] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [showPauseDialog, setShowPauseDialog] = useState(false);
  const [pauseMonths, setPauseMonths] = useState<string>("");

  const handleManageSubscription = async () => {
    try {
      const response = await apiClient.get<{ ok: boolean; url: string }>(API_ROUTES.BILLING_CUSTOMER_PORTAL);
      if (response.ok && response.url) {
        window.open(response.url, '_blank');
      } else {
        throw new Error('Failed to get customer portal URL');
      }
    } catch (error: any) {
      console.error('Error opening customer portal:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to open subscription management",
        variant: "destructive",
      });
    }
  };

  const handlePauseSubscription = async () => {
    if (!pauseMonths) return;

    setIsProcessing(true);
    try {
      const response = await apiClient.post<{ ok: boolean; message: string; resumeAt: string }>(
        API_ROUTES.BILLING_PAUSE,
        { pauseMonths: parseInt(pauseMonths) }
      );

      if (!response.ok) {
        throw new Error(response.message || 'Failed to pause subscription');
      }

      toast({
        title: "Subscription Paused",
        description: response.message || `Your subscription has been paused for ${pauseMonths} month${pauseMonths !== "1" ? "s" : ""}. It will automatically reactivate after this period.`,
      });

      setShowPauseDialog(false);
      setPauseMonths("");

      // Refresh subscription data
      await refreshSubscription();

      // Navigate to account paused page
      navigate('/account-paused');
    } catch (error: any) {
      console.error('Error pausing subscription:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to pause subscription. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCancelSubscription = async () => {
    setIsProcessing(true);
    try {
      const response = await apiClient.post<{ ok: boolean; message: string; cancelAt: string }>(
        API_ROUTES.BILLING_CANCEL
      );

      if (!response.ok) {
        throw new Error(response.message || 'Failed to cancel subscription');
      }

      toast({
        title: "Subscription Cancelled",
        description: response.message || "Your subscription has been cancelled. You'll continue to have access until the end of your current billing period.",
      });

      setShowCancelDialog(false);

      // Refresh subscription data
      await refreshSubscription();

      navigate('/profile?tab=billing');
    } catch (error: any) {
      console.error('Error canceling subscription:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to cancel subscription. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="container mx-auto p-6 max-w-4xl">
      <div className="mb-6">
        <h1 className="text-2xl font-bold">Need to take a break?</h1>
        <p className="text-muted-foreground">Pause your subscription temporarily or manage your subscription settings.</p>
      </div>

      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/profile?tab=billing')}
          className="flex items-center gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </Button>
      </div>

      <div className="space-y-6">
        {/* Current Subscription Overview */}
        <Card>
          <CardHeader>
            <CardTitle>Current Subscription</CardTitle>
            <CardDescription>
              Your active subscription details
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                <p className="text-muted-foreground mt-2">Loading subscription data...</p>
              </div>
            ) : usage.subscribed ? (
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="text-lg font-semibold">{usage.subscriptionTier || usage.planType || 'Pro Plan'}</h3>
                    <p className="text-muted-foreground">
                      {usage.maxUsers || 1} user{usage.maxUsers !== 1 ? 's' : ''} included - Monthly billing
                    </p>
                  </div>
                  <Badge
                    variant="secondary"
                    className={
                      usage.is_paused
                        ? "text-yellow-700 bg-yellow-50 px-3 py-1"
                        : "text-green-700 bg-green-50 px-3 py-1"
                    }
                  >
                    {usage.is_paused ? 'Paused' : 'Active'}
                  </Badge>
                </div>
                {usage.nextBillingDate && !usage.is_paused && (
                  <div className="text-sm text-muted-foreground">
                    Next billing date: {new Date(usage.nextBillingDate).toLocaleDateString()}
                  </div>
                )}
                {usage.is_paused && usage.pauseEndDate && (
                  <div className="text-sm text-muted-foreground">
                    Subscription will resume on: {new Date(usage.pauseEndDate).toLocaleDateString()}
                  </div>
                )}
                <div className="pt-4">
                  <Button className="w-full" variant="outline" onClick={handleManageSubscription}>
                    Manage Subscription
                  </Button>
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-muted-foreground mb-4">No active subscription found</p>
                <Button variant="outline" onClick={() => navigate('/profile?tab=billing&from=manage-subscription')}>
                  View Plans
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Pause Subscription - Main Option */}
        <Card>
          <CardHeader>
            <CardTitle>
              Pause Subscription
            </CardTitle>
            <CardDescription>
              Take a temporary break from your subscription
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3">
              <div>
                <p className="font-medium">Flexible pause options</p>
                <p className="text-sm text-muted-foreground">
                  Pause your subscription for up to 6 months. Perfect if you need a temporary break but plan to return.
                </p>
              </div>
              
              <div>
                <p className="font-medium">Easy reactivation</p>
                <p className="text-sm text-muted-foreground">
                  Your subscription will automatically reactivate after your selected pause period and you'll regain full access.
                </p>
              </div>
            </div>

            <Separator />

            <div className="flex justify-center pt-2">
              <Button 
                onClick={() => setShowPauseDialog(true)}
                disabled={isProcessing}
                className="px-6 py-2"
              >
                Pause My Subscription
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Cancel Subscription - Secondary Option */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
            <CardTitle>
              Cancel Subscription
            </CardTitle>
            <Button 
              variant="outline"
              size="sm"
              onClick={() => setShowCancelDialog(true)}
              disabled={isProcessing}
            >
              Cancel Subscription
            </Button>
          </CardHeader>
        </Card>

        {/* Cancel Confirmation Dialog */}
        <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Are you sure?</DialogTitle>
              <DialogDescription>
                Are you sure you want to cancel your subscription? You will lose access to all premium features.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-2">
              <Button 
                variant="outline" 
                onClick={() => setShowCancelDialog(false)}
                disabled={isProcessing}
              >
                Go Back
              </Button>
              <Button 
                onClick={handleCancelSubscription}
                disabled={isProcessing}
              >
                {isProcessing ? "Processing..." : "Confirm Cancel"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Pause Subscription Dialog */}
        <Dialog open={showPauseDialog} onOpenChange={setShowPauseDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Pause Subscription</DialogTitle>
              <DialogDescription>
                Choose how long you'd like to pause your subscription. Your account will automatically reactivate after this period and you'll regain full access (and be charged).
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <Select value={pauseMonths} onValueChange={setPauseMonths}>
                <SelectTrigger>
                  <SelectValue placeholder="Select pause duration" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1 month</SelectItem>
                  <SelectItem value="2">2 months</SelectItem>
                  <SelectItem value="3">3 months</SelectItem>
                  <SelectItem value="4">4 months</SelectItem>
                  <SelectItem value="5">5 months</SelectItem>
                  <SelectItem value="6">6 months</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <DialogFooter className="gap-2">
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowPauseDialog(false);
                  setPauseMonths("");
                }}
                disabled={isProcessing}
              >
                Cancel
              </Button>
              <Button 
                onClick={handlePauseSubscription}
                disabled={isProcessing || !pauseMonths}
              >
                {isProcessing ? "Processing..." : "Pause Subscription"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}